import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel } from '@mui/material';
import { InspectionEvaluationIssue } from '../../models/inspectionEvaluationIssue';

type InspectionEvaluationIssueDisplayProps = InspectionEvaluationIssue & {
    fetchSolution?: () => void;
    onToggleHidden?: () => void;
    furtherEvaluationNeeded?: string[]; // New field for follow-up items
};

const getSeverityColor = (severity: number) => {
  if (severity > 7) return '#FF4C4C';
  if (severity > 4) return '#FFA500';
  return '#4CAF50';
};

const getUrgencyColor = (urgency: number) => {
  if (urgency > 7) return '#FF4C4C';
  if (urgency > 4) return '#FFA500';
  return '#4CAF50';
};

const InspectionEvaluationIssueDisplay: React.FC<InspectionEvaluationIssueDisplayProps> = ({
  shortDescription,
  location,
  longDescription,
  severity,
  urgency,
  highcostToRepair,
  lowcostToRepair,
  fetchSolution,
  hidden,
  onToggleHidden,
  furtherEvaluationNeeded, // Initialize as an empty array
}) => {
  const [isHidden, setIsHidden] = useState(hidden);

  useEffect(() => {
    setIsHidden(hidden);
  }, [hidden]);

  const handleToggle = () => {
    setIsHidden(!isHidden);
    if (onToggleHidden) onToggleHidden();
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.shortDescription}>{shortDescription}</Typography>
      <Typography sx={styles.location}><strong>Location:</strong> {location}</Typography>
      <Box sx={{ display: 'flex', gap: 1, marginY: 1 }}>
        <Box sx={{ ...styles.tag, backgroundColor: getSeverityColor(severity) }}>
          {severity > 7 ? 'High Severity' : severity > 4 ? 'Moderate Severity' : 'Low Severity'}
        </Box>
        <Box sx={{ ...styles.tag, backgroundColor: getUrgencyColor(urgency) }}>
          {urgency > 7 ? 'High Urgency' : urgency > 4 ? 'Moderate Urgency' : 'Low Urgency'}
        </Box>
      </Box>
      <Typography sx={styles.longDescription}>{longDescription}</Typography>
      <Typography sx={styles.cost}>
            <strong>Cost Range:</strong> ${lowcostToRepair.toLocaleString()} - ${highcostToRepair.toLocaleString()}
      </Typography>
      
      {/* Follow-up needed items */}
      <Box sx={styles.evaluation}>
        <Typography component="span" sx={styles.evaluationLabel}>Requires Follow-up:</Typography>
        {furtherEvaluationNeeded?.length > 0 ? (
          <Box component="ul" sx={styles.evaluationList}>
            {furtherEvaluationNeeded.map((item, index) => (
              <Typography component="li" key={index} sx={styles.evaluationItem}>
                {item}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography component="span" sx={styles.evaluationValue}>None</Typography>
        )}
      </Box>

      <Box sx={styles.buttonContainer}>
        {fetchSolution && (
          <Button variant="outlined" size="small" onClick={fetchSolution}>
            View Details
          </Button>
        )}
        {onToggleHidden && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isHidden}
                onChange={handleToggle}
              />
            }
            label="Hide"
            sx={{ marginLeft: 2 }}
          />
        )}
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    width: '100%',
    padding: '8px 0',
    fontFamily: 'Arial, sans-serif',
    color: '#333',
  },
  shortDescription: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  location: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px',
  },
  tag: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#fff',
  },
  longDescription: {
    fontSize: '14px',
    lineHeight: '1.4',
    marginBottom: '8px',
  },
  cost: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  evaluation: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    marginTop: '8px',
  },
  evaluationLabel: {
    marginRight: '8px',
    color: '#666',
  },
  evaluationList: {
    margin: 0,
    paddingLeft: '16px',
    listStyleType: 'disc',
  },
  evaluationItem: {
    lineHeight: '1.4',
    listStyleType: 'none',
  },
  evaluationValue: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 2,
  },
};

export default InspectionEvaluationIssueDisplay;
